/* sugar:assets/sugar-pricing.js@2.2.2 */
/*
 * sugar-pricing — one live price for every block that shows one.
 *
 * A product page can show the same number in four places: the price, the subtotal, the buy
 * button and the sticky bar. Each of them used to listen to the same five events and redo the
 * same arithmetic, which is how they drifted apart. This module does the arithmetic once per
 * product and hands the answer to whoever subscribed.
 *
 *   const store = SugarPricing.store({ sectionId, productId, price, compareAt, variantIds });
 *   store.subscribe((state) => { ... });
 *
 * state: { quantity, unitPrice, totalPrice, totalCompare, onSale, available, unitRatio }
 */
(function () {
  if (window.SugarPricing) return;

  const stores = new Map();

  /* ---------------------------------------------------------------- money */

  const DEFAULT_FORMAT = '${{amount}}';

  /*
   * Shopify's money format string, not the browser's idea of a currency. A shop set to
   * "1.234,00 €" or to whole pounds has to keep looking that way after a variant change,
   * and Intl can't be told to do either. `whole` drops the cents (the *_rounded tokens).
   */
  function formatMoney(cents, format, whole) {
    const value = typeof cents === 'string' ? cents.replace(/[^0-9.-]/g, '') : cents;
    let amount = Number(value);
    if (!Number.isFinite(amount)) return '';
    // Whole rounds in currency, not in cents: 1234 cents becomes 12, not 12 with hidden cents.
    if (whole) amount = Math.round(amount / 100) * 100;

    // A legacy format can wrap the amount in markup (<span class=money>…); prices here are text.
    const template = String(format || DEFAULT_FORMAT).replace(/<[^>]*>/g, '');
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const match = template.match(placeholder);
    if (!match) return template;

    const styles = {
      amount: [2, ',', '.'],
      amount_no_decimals: [0, ',', '.'],
      amount_with_comma_separator: [2, '.', ','],
      amount_no_decimals_with_comma_separator: [0, '.', ','],
      amount_with_apostrophe_separator: [2, "'", '.'],
      amount_with_space_separator: [2, ' ', ','],
      amount_no_decimals_with_space_separator: [0, ' ', ','],
      amount_with_period_and_space_separator: [2, ' ', '.'],
    };
    const style = styles[match[1]] || styles.amount;
    const formatted = group(amount, whole ? 0 : style[0], style[1], style[2]);

    return template.replace(placeholder, formatted);
  }

  /* Matches Liquid's money_without_trailing_zeros: a whole amount loses its ".00". */
  function dropZeroDecimals(money) {
    return money.replace(/([.,])00(?!\d)/, '');
  }

  function group(cents, decimals, thousands, decimal) {
    const fixed = (cents / 100).toFixed(decimals);
    const parts = fixed.split('.');
    const whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return parts[1] ? whole + decimal + parts[1] : whole;
  }

  /* ------------------------------------------------------------ discounts */

  function applySellingPlan(price, plan) {
    const adjustment = plan && plan.price_adjustments && plan.price_adjustments[0];
    if (!adjustment) return price;
    if (adjustment.value_type === 'percentage') {
      return Math.round((price * (100 - adjustment.value)) / 100);
    }
    if (adjustment.value_type === 'fixed_amount') {
      return Math.max(0, price - adjustment.value);
    }
    if (adjustment.value_type === 'price') {
      return adjustment.value;
    }
    return price;
  }

  function applyOffer(price, offer) {
    if (!offer || !offer.type || !(offer.value > 0)) return price;
    /* Per unit, the part of a cent dropped from the discount, the way checkout charges it. */
    if (offer.type === 'percentage') {
      return price - Math.floor((price * Math.round(offer.value * 100)) / 10000);
    }
    if (offer.type === 'fixed' && offer.scope === 'per_item') {
      return Math.max(0, price - offer.value);
    }
    /* A fixed discount off the order total comes off once, at the total. */
    return price;
  }

  /* Amount off and Set price are typed in the store's currency; abroad they convert at the market's rate. */
  function shopperDiscountValue(type, value) {
    if (type !== 'fixed' && type !== 'set_price') return value;
    const rate = parseFloat(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1;
    return rate === 1 ? value : Math.round(value * rate);
  }

  function readOffer(el) {
    if (!el) return null;
    const type = el.dataset.offerDiscountType || null;
    return {
      type,
      value: shopperDiscountValue(type, parseFloat(el.dataset.offerDiscountValue) || 0),
      scope: el.dataset.offerDiscountScope || 'total',
    };
  }

  /*
   * An offer tier publishes what it costs in full, so its own maths — mix-and-match, tiered
   * per-unit prices, a free gift — wins over anything recomputed from the unit price. The
   * "requested" figures include units a quantity picker stacked on top of the tier.
   */
  function firstFinite() {
    for (let i = 0; i < arguments.length; i++) {
      const n = typeof arguments[i] === 'number' ? arguments[i] : parseInt(arguments[i], 10);
      if (Number.isFinite(n)) return n;
    }
    return 0;
  }

  /*
   * A bundle builder in the section owns the quantity and the total while it holds a pick:
   * it writes what its rows add up to on its root and announces it with bundle:changed.
   */
  function readBundleTotals(sectionEl) {
    const bundles = sectionEl ? Array.from(sectionEl.querySelectorAll('[data-bundle-builder]')) : [];
    /* A builder in a popup leaves the section once the popup first opens (popup.js moves it to <body>). */
    const sid = sectionEl && sectionEl.id.replace(/^shopify-section-/, '');
    if (sid) {
      document.querySelectorAll('[data-bundle-builder][data-section-id="' + sid + '"]').forEach((el) => {
        if (bundles.indexOf(el) === -1) bundles.push(el);
      });
    }
    if (!bundles.length) return null;
    /* Several in one section: the one the shopper touched last speaks for the section. */
    const bundle = bundles.reduce((best, el) => {
      const stamp = parseInt(el.dataset.bbUpdated, 10) || 0;
      return !best || stamp > (parseInt(best.dataset.bbUpdated, 10) || 0) ? el : best;
    }, null);
    const count = parseInt(bundle.dataset.bbCount, 10);
    const total = parseInt(bundle.dataset.bbTotal, 10);
    if (!(count > 0) || !Number.isFinite(total)) return null;
    const compare = parseInt(bundle.dataset.bbCompare, 10);
    const oneTime = parseInt(bundle.dataset.bbOnetimeTotal, 10);
    const oneTimeCompare = parseInt(bundle.dataset.bbOnetimeCompare, 10);
    return {
      quantity: count,
      totalPrice: Math.max(0, total),
      totalCompare: Number.isFinite(compare) ? Math.max(0, compare) : Math.max(0, total),
      oneTimeTotal: Number.isFinite(oneTime) ? Math.max(0, oneTime) : Math.max(0, total),
      oneTimeCompare: Number.isFinite(oneTimeCompare) ? Math.max(0, oneTimeCompare) : Math.max(0, total),
    };
  }

  function readOfferTotals(sectionEl, fallbackQuantity) {
    const offer = sectionEl && sectionEl.querySelector('.offer.is-selected');
    if (!offer) return null;

    const tierQuantity = Math.max(parseInt(offer.dataset.quantity, 10) || fallbackQuantity || 1, 1);
    const requestedQuantity = parseInt(offer.dataset.computedRequestedQuantity, 10);
    const requestedTotal = parseInt(offer.dataset.computedRequestedTotalPrice, 10);
    const requestedCompare = parseInt(offer.dataset.computedRequestedComparePrice, 10);

    const totalPrice = Number.isFinite(requestedTotal)
      ? requestedTotal
      : parseInt(offer.dataset.computedTotalPrice, 10);
    if (!Number.isFinite(totalPrice)) return null;

    const compare = Number.isFinite(requestedCompare)
      ? requestedCompare
      : parseInt(offer.dataset.computedComparePrice, 10);

    /* The same tier priced without the page's plan; absent on an older offers block. */
    const oneTime = firstFinite(
      offer.dataset.computedRequestedOneTimeTotalPrice,
      offer.dataset.computedOneTimeTotalPrice,
      totalPrice
    );
    const oneTimeCompare = firstFinite(
      offer.dataset.computedRequestedOneTimeComparePrice,
      offer.dataset.computedOneTimeComparePrice,
      Number.isFinite(compare) ? compare : totalPrice
    );

    return {
      quantity: Number.isFinite(requestedQuantity) && requestedQuantity > 0 ? requestedQuantity : tierQuantity,
      totalPrice: Math.max(0, totalPrice),
      totalCompare: Number.isFinite(compare) ? Math.max(0, compare) : Math.max(0, totalPrice),
      oneTimeTotal: Math.max(0, oneTime),
      oneTimeCompare: Math.max(0, oneTimeCompare),
    };
  }

  /* --------------------------------------------------------------- tokens */

  const TOKEN_TEST = /\[(percentage|percentage_rounded|amount|amount_saved|amount_rounded|price|subtotal|savings|compare_price|quantity)\]/;

  /*
   * The per-unit tokens: [price_per:N] is one item at the page's price divided by N (a bag that
   * lasts 24 days reads "$1.04/day" at the 4-bag tier), [unit_price] is the same item divided by
   * the variant's Shopify unit price measure ("$3.50 per serving" for a 24-count pack). They
   * describe the price, not a saving, so a label that holds only these shows at full price too.
   */
  const PER_TEST = /\[(price_per:\d+|unit_price)\]/;

  function hasTokens(text) {
    return TOKEN_TEST.test(text || '');
  }

  function hasPerTokens(text) {
    return PER_TEST.test(text || '');
  }

  /*
   * Every token is replaced everywhere it appears, which is what Liquid's `replace` does on
   * the server. Substituting only the first one made a label change shape on the first update.
   */
  function fillTokens(template, state, format) {
    if (!template) return '';
    const saved = Math.max(0, state.totalCompare - state.totalPrice);
    const percentage = state.totalCompare > 0 ? (saved / state.totalCompare) * 100 : 0;
    const money = (cents) => formatMoney(cents, format);

    const values = {
      '[percentage]': percentage.toFixed(1) + '%',
      '[percentage_rounded]': Math.round(percentage) + '%',
      '[amount_saved]': money(saved),
      '[amount]': money(saved),
      '[savings]': money(saved),
      '[amount_rounded]': dropZeroDecimals(formatMoney(Math.round(saved / 100) * 100, format)),
      '[price]': money(state.totalPrice),
      '[subtotal]': money(state.totalPrice),
      '[compare_price]': money(state.totalCompare),
      '[quantity]': String(state.quantity),
    };

    return fillPerTokens(
      template.replace(
        /\[(percentage_rounded|percentage|amount_saved|amount_rounded|amount|savings|compare_price|subtotal|price|quantity)\]/g,
        (token) => values[token]
      ),
      state,
      format
    );
  }

  function fillPerTokens(template, state, format, whole) {
    if (!hasPerTokens(template)) return template;
    const each = state.quantity > 0 ? state.totalPrice / state.quantity : state.totalPrice;
    const money = (cents) => {
      const rounded = Math.round(cents);
      return formatMoney(rounded, format, !!whole && rounded % 100 === 0);
    };
    return template
      .replace(/\[price_per:(\d+)\]/g, (token, n) => {
        const d = parseInt(n, 10);
        return d > 0 ? money(each / d) : token;
      })
      .replace(/\[unit_price\]/g, () => (state.unitRatio > 0 ? money(each * state.unitRatio) : ''));
  }

  /* ---------------------------------------------------------------- store */

  function createStore(init) {
    const sectionId = String(init.sectionId);
    /*
     * Resolved on every use, never captured. The theme editor re-renders a section by replacing
     * the element, and a held reference goes on quietly answering questions about a detached
     * copy of the page — stale offer tiers, a stale quantity, prices that stop moving.
     */
    const sectionEl = () => document.getElementById('shopify-section-' + sectionId);
    const variantIds = new Set((init.variantIds || []).map(String));
    const subscribers = new Set();
    /* Each variant's Shopify unit price measure as reference ÷ quantity, for [unit_price]. */
    const units = Object.assign({}, init.units || {});
    let variantId = init.variantId ? String(init.variantId) : '';

    const store = {
      get sectionEl() {
        return sectionEl();
      },
      format: init.format,
      state: {
        quantity: 1,
        unitPrice: init.price || 0,
        totalPrice: init.price || 0,
        totalCompare: init.compareAt || init.price || 0,
        onSale: (init.compareAt || 0) > (init.price || 0),
        available: init.available !== false,
      },
    };

    let basePrice = init.price || 0;
    let baseCompare = init.compareAt || 0;
    let available = init.available !== false;
    let quantity = 1;
    let offer = null;
    let sellingPlan = null;
    let sellingPlanId = null;
    let subscribed = false;
    let compareBasis = 'onetime';
    let queued = false;

    /*
     * The page's number and the one-time number, computed side by side. They differ only when a
     * subscription plan is active on the page: a button that sells outside it (the "buy it once"
     * pattern) still has to follow the offer tier and the quantity, just not the plan.
     */
    function totalsFor(withPlan) {
      let unit = basePrice;
      if (withPlan && subscribed && sellingPlan) unit = applySellingPlan(unit, sellingPlan);
      unit = applyOffer(unit, offer);

      let total = unit * quantity;
      if (offer && offer.type === 'fixed' && offer.value > 0 && offer.scope === 'total') {
        total = Math.max(0, total - offer.value);
      }

      /*
       * What a subscription is compared against is the merchant's call: the one-time price they
       * would otherwise pay, the price before any offer discount, or the MSRP.
       */
      let compareUnit;
      if (withPlan && subscribed && sellingPlan) {
        if ((compareBasis === 'maximum' || compareBasis === 'compare_at') && baseCompare > 0) {
          compareUnit = baseCompare;
        } else if (compareBasis === 'before_discounts') {
          compareUnit = basePrice;
        } else {
          compareUnit = applyOffer(basePrice, offer);
        }
      } else {
        compareUnit = baseCompare > 0 ? baseCompare : basePrice;
      }

      return { total, compare: compareUnit * quantity };
    }

    function compute() {
      const live = sectionEl();
      /*
       * The selected tier's discount is read fresh here rather than carried from the last event:
       * a tier change that arrives without a block id used to leave the previous tier's discount
       * in place, and the fallback maths then priced the new quantity at the old discount.
       */
      const selectedOffer = live && live.querySelector('.offer.is-selected');
      if (selectedOffer) offer = readOffer(selectedOffer);

      const offerTotals = readBundleTotals(live) || readOfferTotals(live, quantity);
      let totalPrice;
      let totalCompare;
      let oneTimeTotal;
      let oneTimeCompare;

      if (offerTotals) {
        /* A tier publishes what it costs in full, plan or no plan. */
        quantity = offerTotals.quantity;
        totalPrice = offerTotals.totalPrice;
        totalCompare = offerTotals.totalCompare > 0 ? offerTotals.totalCompare : offerTotals.totalPrice;
        oneTimeTotal = offerTotals.oneTimeTotal;
        oneTimeCompare = offerTotals.oneTimeCompare > 0 ? offerTotals.oneTimeCompare : offerTotals.oneTimeTotal;
      } else {
        const withPlan = totalsFor(true);
        const oneTime = totalsFor(false);
        totalPrice = withPlan.total;
        totalCompare = withPlan.compare;
        oneTimeTotal = oneTime.total;
        oneTimeCompare = oneTime.compare;
      }

      const perUnit = (n) => (quantity > 0 ? Math.round(n / quantity) : n);
      store.state = {
        quantity,
        unitPrice: perUnit(totalPrice),
        totalPrice,
        totalCompare,
        onSale: totalCompare > totalPrice,
        available,
        /* The page's subscription, for a subscriber that wants to add the same plan. */
        subscribed,
        sellingPlanId: subscribed ? sellingPlanId : null,
        /* The same order without the page's subscription plan. */
        oneTimeTotal,
        oneTimeCompare,
        oneTimeUnit: perUnit(oneTimeTotal),
        /* The variant as it stands, nothing applied — a caller with its own plan starts here. */
        baseUnit: basePrice,
        baseCompareUnit: baseCompare,
        variantId,
        unitRatio: units[variantId] || 0,
      };

      subscribers.forEach((fn) => fn(store.state));
    }

    /*
     * Five events can land in the same tick; the shopper only sees the last one. A microtask,
     * not a frame — a background tab stops painting, and a stale price would still be there
     * when the shopper came back to it.
     */
    function schedule() {
      if (queued) return;
      queued = true;
      Promise.resolve().then(() => {
        queued = false;
        compute();
      });
    }

    /*
     * With an offers block on the page the quantity belongs to it first: it picks the tier that
     * quantity qualifies for, keeps the leftover units as extras, and republishes the totals
     * this store reads. Applying the number here instead would leave the offer's own totals
     * describing the old quantity, and those totals win.
     */
    function offersController() {
      const el = sectionEl();
      const root = el && el.querySelector('[data-sugar-offers]');
      const controller = root && root.sugarOffers;
      return controller && typeof controller.setRequestedQuantity === 'function' ? controller : null;
    }

    /* Every block on the page brings the measures it printed; the first store keeps them all. */
    store.addUnits = function (more, openingVariant) {
      if (more) Object.assign(units, more);
      if (!variantId && openingVariant) variantId = String(openingVariant);
      if ((units[variantId] || 0) !== (store.state.unitRatio || 0)) schedule();
    };

    store.subscribe = function (fn) {
      subscribers.add(fn);
      fn(store.state);
      return () => subscribers.delete(fn);
    };
    store.refresh = schedule;
    store.setQuantity = function (next) {
      const parsed = parseInt(next, 10);
      if (!(parsed > 0)) return;

      /* A bundle builder's count is the quantity; a picker elsewhere can't move it. */
      if (readBundleTotals(sectionEl())) return;

      const controller = offersController();
      if (controller) {
        /*
         * It answers with offers:quantity-changed, which lands back here. It also writes the
         * quantity input, and the change that provokes settles on the second pass: the tier and
         * the requested quantity both match by then, so it publishes nothing further.
         */
        controller.setRequestedQuantity(parsed);
        return;
      }

      if (parsed !== quantity) {
        quantity = parsed;
        schedule();
      }
    };

    function sameSection(detail) {
      return !detail || !detail.sectionId || String(detail.sectionId) === sectionId;
    }

    document.addEventListener('variant:change', (e) => {
      const detail = e.detail || {};
      if (!sameSection(detail) || !detail.variant) return;
      if (variantIds.size && !variantIds.has(String(detail.variant.id))) return;
      variantId = String(detail.variant.id);
      basePrice = detail.variant.price;
      baseCompare = detail.variant.compare_at_price || 0;
      available = detail.variant.available !== false;
      schedule();
    });

    document.addEventListener('offers:quantity-changed', (e) => {
      const detail = e.detail || {};
      if (!sameSection(detail)) return;
      const scoped = detail.blockId
        ? (sectionEl() || document).querySelector('.offer[data-block-id="' + detail.blockId + '"]')
        : null;
      if (!detail.sectionId && detail.blockId && !scoped) return;
      if (!detail.quantity) return;
      quantity = parseInt(detail.quantity, 10) || 1;
      if (scoped) offer = readOffer(scoped);
      schedule();
    });

    document.addEventListener('offers:prices-updated', (e) => {
      if (sameSection(e.detail)) schedule();
    });

    document.addEventListener('bundle:changed', (e) => {
      if (sameSection(e.detail)) schedule();
    });

    document.addEventListener('subscription:type-changed', (e) => {
      const detail = e.detail || {};
      if (!sameSection(detail)) return;
      subscribed = detail.type === 'subscribe';
      sellingPlan = detail.sellingPlan || null;
      sellingPlanId = detail.sellingPlanId || (sellingPlan && sellingPlan.id) || null;
      compareBasis = detail.compareBasis || 'onetime';
      if (detail.quantity > 0) quantity = detail.quantity;
      schedule();
    });

    /*
     * Quantity pickers publish no event of their own, so read them where they sit. Delegated
     * from the section, so a picker the Section Rendering API swaps out keeps working.
     */
    const QUANTITY_HOSTS = '.product-quantity-selector-block, .sugar-product-buy-button__qty';
    const QUANTITY_FIELD = QUANTITY_HOSTS.split(', ')
      .map((host) => host + ' .quantity__input, ' + host + ' .quantity select')
      .join(', ');

    function inSection(node) {
      const el = sectionEl();
      return !!(el && node && el.contains(node));
    }

    /* Delegated from the document, so a re-rendered section keeps its quantity picker working. */
    function watchQuantity() {
      document.addEventListener('change', (e) => {
        const t = e.target;
        if (t && t.matches && t.matches(QUANTITY_FIELD) && inSection(t)) store.setQuantity(t.value);
      });

      document.addEventListener('click', (e) => {
        const stepper = e.target && e.target.closest && e.target.closest('quantity-input');
        if (!stepper || !stepper.closest(QUANTITY_HOSTS) || !inSection(stepper)) return;
        const input = stepper.querySelector('.quantity__input');
        /* The stepper writes the input after its own click handler runs. */
        if (input) setTimeout(() => store.setQuantity(input.value), 10);
      });
    }

    /*
     * The offers block marks its opening tier while rendering, so a page that loads on a
     * 3-for-2 shows that price rather than the single-unit one for a frame.
     */
    function readInitialState() {
      const live = sectionEl();
      let state = (window.subscriptionStateBySection || {})[sectionId] || window.subscriptionState;
      /*
       * A Silent Subscription block seeds the same state from its own script, which may not
       * have run yet; its config is on the page already, so the first paint is the plan's price.
       */
      const silent = !state && live && live.querySelector('[data-silent-subscription-config]');
      if (silent && !live.querySelector('[data-sugar-subscriptions]')) {
        try {
          const cfg = JSON.parse(silent.dataset.silentSubscriptionConfig);
          const plan = cfg.plan;
          if (plan) state = { type: 'subscribe', sellingPlan: plan, sellingPlanId: plan.id, compareBasis: cfg.compareBasis || 'onetime' };
        } catch (e) {
          /* the block's own script announces it */
        }
      }
      if (state) {
        subscribed = state.type === 'subscribe';
        sellingPlan = state.sellingPlan || null;
        sellingPlanId = state.sellingPlanId || state.planId || (sellingPlan && sellingPlan.id) || null;
        compareBasis = state.compareBasis || 'onetime';
        if (state.quantity > 0) quantity = state.quantity;
      }
      const selected = live && live.querySelector('.offer.is-selected');
      if (selected) {
        const offerQuantity = parseInt(selected.dataset.quantity, 10) || 0;
        if (offerQuantity > 0) quantity = offerQuantity;
        offer = readOffer(selected);
      }
      compute();
    }

    function start() {
      watchQuantity();
      readInitialState();
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', start);
    } else {
      start();
    }

    return store;
  }

  window.SugarPricing = {
    formatMoney,
    fillTokens,
    fillPerTokens,
    hasTokens,
    hasPerTokens,
    applySellingPlan,
    store(init) {
      const key = init.sectionId + ':' + (init.productId || '');
      let store = stores.get(key);
      if (!store) {
        store = createStore(init);
        stores.set(key, store);
      } else if (init.units || init.variantId) {
        store.addUnits(init.units, init.variantId);
      }
      return store;
    },
  };
})();
